import {
  MonitorIcon,
  MoonIcon,
  SunIcon,
  CheckIcon,
} from '@phosphor-icons/react'
import { SidebarTheme } from '@scrambl/shared';
import { PageContainer } from '@/components/layout/PageContainer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SIDEBAR_THEMES } from '@/store/ui-preferences.store';
import { useThemePreferences } from '@/hooks/useThemePreferences';
import { cn } from '@/lib/utils';

export default function SettingsPage() {
  const {
    themeMode,
    sidebarTheme,
    isDarkMode,
    currentSidebarColor,
    setThemeMode,
    setSidebarTheme,
  } = useThemePreferences();

  // Get current theme config for text color
  const currentThemeConfig = SIDEBAR_THEMES.find(t => t.id === sidebarTheme) || SIDEBAR_THEMES[0];
  const useDarkText = isDarkMode ? false : currentThemeConfig.lightTextColor === 'dark';

  return (
    <PageContainer
      header={{
        title: "Settings",
        description: "Manage your application preferences"
      }}
      className="space-y-6"
    >
      <Card>
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
          <CardDescription>
            Customize how the application looks on your device
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Light/Dark Mode */}
            <div>
              <h3 className="text-sm font-medium mb-3">Color Mode</h3>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant={themeMode === 'light' ? 'default' : 'outline'}
                  className="gap-2"
                  onClick={() => setThemeMode('light')}
                >
                  <SunIcon className="h-4 w-4" />
                  Light
                </Button>
                <Button
                  variant={themeMode === 'dark' ? 'default' : 'outline'}
                  className="gap-2"
                  onClick={() => setThemeMode('dark')}
                >
                  <MoonIcon className="h-4 w-4" />
                  Dark
                </Button>
                <Button
                  variant={themeMode === 'system' ? 'default' : 'outline'}
                  className="gap-2"
                  onClick={() => setThemeMode('system')}
                >
                  <MonitorIcon className="h-4 w-4" />
                  System
                </Button>
              </div>
            </div>

            {/* Theme */}
            <div>
              <h3 className="text-sm font-medium mb-3">Theme</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Choose a color theme for the sidebar and title bar. Colors automatically adjust for light/dark mode.
              </p>
              <div className="grid grid-cols-5 gap-3 max-w-xl">
                {SIDEBAR_THEMES.map((themeOption) => (
                  <button
                    key={themeOption.id}
                    onClick={() => setSidebarTheme(themeOption.id)}
                    className={cn(
                      "relative flex flex-col items-center gap-2 p-2 sm:p-3 rounded-lg border-2 transition-all duration-200 hover:scale-105",
                      sidebarTheme === themeOption.id 
                        ? "border-primary ring-2 ring-primary/20" 
                        : "border-border hover:border-primary/50"
                    )}
                  >
                    {/* Show both light and dark variants stacked */}
                    <div className="relative w-8 h-8 sm:w-12 sm:h-12 rounded-lg shadow-md border border-black/10 overflow-hidden">
                      {/* Light mode color (top half) */}
                      <div 
                        className="absolute inset-0 h-1/2"
                        style={{ backgroundColor: themeOption.lightColor }}
                      />
                      {/* Dark mode color (bottom half) */}
                      <div 
                        className="absolute inset-0 top-1/2 h-1/2"
                        style={{ backgroundColor: themeOption.darkColor }}
                      />
                      {/* Diagonal line separator */}
                      <div className="absolute inset-0 border-b border-white/30 h-1/2" />
                      {sidebarTheme === themeOption.id && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <CheckIcon 
                            className="h-4 w-4 sm:h-5 sm:w-5 text-white drop-shadow-md"
                            weight="bold" 
                          />
                        </div>
                      )}
                    </div>
                    <span className="text-[10px] sm:text-xs font-medium text-center leading-tight line-clamp-2">
                      {themeOption.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-2">
              <p className="text-sm text-muted-foreground">
                These settings are saved per organization, allowing you to have different themes for each workspace.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Theme Preview */}
      <Card>
        <CardHeader>
          <CardTitle>Theme Preview</CardTitle>
          <CardDescription>
            Preview your current theme settings ({isDarkMode ? 'Dark' : 'Light'} mode)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            {/* Sidebar Preview */}
            <div 
              className="w-16 h-32 rounded-lg shadow-lg flex flex-col items-center pt-3 gap-2 transition-colors duration-300"
              style={{ backgroundColor: currentSidebarColor }}
            >
              <div 
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold",
                  useDarkText 
                    ? "bg-black/10 text-black/70" 
                    : "bg-white/10 text-white/70"
                )}
              >
                S
              </div>
              <div className={cn(
                "w-10 h-1 rounded-full",
                useDarkText 
                  ? "bg-black/20" 
                  : "bg-white/20"
              )} />
              <div className={cn(
                "w-8 h-1 rounded-full",
                useDarkText 
                  ? "bg-black/15" 
                  : "bg-white/15"
              )} />
              <div className={cn(
                "w-10 h-1 rounded-full",
                useDarkText 
                  ? "bg-black/20" 
                  : "bg-white/20"
              )} />
            </div>
            
            {/* Content Preview */}
            <div className="flex-1 h-32 bg-muted rounded-lg p-3 flex flex-col gap-2">
              <div className="h-3 w-24 bg-foreground/20 rounded" />
              <div className="h-2 w-full bg-foreground/10 rounded" />
              <div className="h-2 w-3/4 bg-foreground/10 rounded" />
              <div className="flex gap-2 mt-auto">
                <div className="h-6 w-16 bg-primary rounded" />
                <div className="h-6 w-16 bg-muted-foreground/20 rounded" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
